/**
 * Wrap a dynamic `import()` factory with exponential backoff so transient
 * chunk-load failures (flaky network, CDN 5xx, deploy churn) don't surface
 * to React.lazy as an unrecoverable rejection.
 *
 * Only retries on classic chunk-load errors — other errors (syntax, runtime
 * in the imported module) bubble up immediately so they hit the boundary and
 * get logged instead of silently retried.
 */
type Importer<T> = () => Promise<T>;

const isChunkLoadError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; message?: string; code?: string };
  const msg = `${e.name ?? ''} ${e.message ?? ''} ${e.code ?? ''}`.toLowerCase();
  return (
    msg.includes('chunkloaderror') ||
    msg.includes('loading chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('failed to import') ||
    msg.includes('importing a module script failed') ||
    msg.includes('dynamically imported module') ||
    msg.includes('unable to preload css') ||
    msg.includes('load failed')
  );
};

export interface LazyRetryOptions {
  retries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  /**
   * Per-attempt timeout. If a single import() attempt does not settle
   * within this window we synthesize a ChunkLoadError and either retry
   * (subject to `retries`) or reject so the error boundary can render
   * its fallback UI. Prevents users from hanging forever on a stalled
   * network without any resolution or rejection.
   */
  timeoutMs?: number;
}

class ChunkLoadTimeoutError extends Error {
  name = 'ChunkLoadError';
  constructor(ms: number) {
    super(`Loading chunk timed out after ${ms}ms`);
  }
}

export function lazyRetry<T>(
  importer: Importer<T>,
  opts: LazyRetryOptions = {}
): Importer<T> {
  const retries = opts.retries ?? 3;
  const baseDelayMs = opts.baseDelayMs ?? 500;
  const maxDelayMs = opts.maxDelayMs ?? 4000;
  const timeoutMs = opts.timeoutMs ?? 20_000;

  const withTimeout = (): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new ChunkLoadTimeoutError(timeoutMs));
      }, timeoutMs);
      importer().then(
        (v) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(v);
        },
        (err) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(err);
        }
      );
    });

  return () =>
    new Promise<T>((resolve, reject) => {
      const attempt = (n: number) => {
        withTimeout()
          .then(resolve)
          .catch((err) => {
            if (n >= retries || !isChunkLoadError(err)) {
              reject(err);
              return;
            }
            const backoff = Math.min(maxDelayMs, baseDelayMs * 2 ** n);
            const jitter = Math.random() * backoff * 0.3;
            setTimeout(() => attempt(n + 1), backoff + jitter);
          });
      };
      attempt(0);
    });
}
